import { Injectable } from '@angular/core';
import { Router, NavigationEnd } from '@angular/router';
import { filter } from 'rxjs/operators';
import { environment } from '../../../environments/environment';

declare global {
  interface Window {
    dataLayer: unknown[];
    gtag?: (...args: unknown[]) => void;
  }
}

/**
 * Google Analytics 4 (gtag.js) for the dashboard SPA.
 *
 * Angular's client-side router does not trigger gtag's automatic page views, so
 * we configure GA with `send_page_view: false` and emit exactly one `page_view`
 * per completed navigation (`NavigationEnd`) — including the initial load. This
 * gives correct SPA tracking with no double-counting.
 *
 * The measurement ID is the single source of truth in the Angular environment
 * files: production builds use the production property; the development build
 * (which serves both localhost and the dev site) uses the non-production
 * property, so dev/local traffic never reaches production analytics. Analytics
 * is a no-op only when the ID is unset/placeholder.
 */
@Injectable({ providedIn: 'root' })
export class AnalyticsService {
  private started = false;

  constructor(private readonly router: Router) {}

  /** Loads gtag.js once and begins SPA route page-view tracking. Safe to call once at app startup. */
  init(): void {
    if (this.started) return;

    const id = environment.googleAnalyticsId;
    if (!id || id.startsWith('G-REPLACE')) return; // disabled only when unconfigured

    this.started = true;

    // 1. Inject the gtag.js loader exactly once.
    const loader = document.createElement('script');
    loader.async = true;
    loader.src = `https://www.googletagmanager.com/gtag/js?id=${id}`;
    document.head.appendChild(loader);

    // 2. Bootstrap the gtag shim.
    window.dataLayer = window.dataLayer || [];
    window.gtag = function gtag(...args: unknown[]): void { window.dataLayer.push(args); };
    window.gtag('js', new Date());
    // send_page_view:false → page views are emitted manually on each NavigationEnd below.
    window.gtag('config', id, { send_page_view: false, anonymize_ip: true });

    // 3. One page_view per completed navigation (the first NavigationEnd covers the initial load).
    this.router.events
      .pipe(filter((e): e is NavigationEnd => e instanceof NavigationEnd))
      .subscribe((e) => this.trackPageView(e.urlAfterRedirects));
  }

  private trackPageView(path: string): void {
    if (!window.gtag) return; // guard against early render / load-timing
    window.gtag('event', 'page_view', {
      page_location: window.location.href,
      page_path: path,
      page_title: document.title,
    });
  }
}
